(ns core.front.date-util
  (:require [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.event :refer [<sub]]
            [core.front.i18n :as i18n]
            [goog.date]
            [goog.date.Date]
            [goog.date.DateRange]
            [goog.date.DateTime]
            [goog.date.Interval]
            [goog.i18n.DateTimeFormat]
            [goog.string :as gstr]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(def preferred-locale (.-language js/window.navigator))


(def system-locale (if (= i18n-model/default-lang :en-us)
                     "en-US"
                     "en-CA"))


(def browser-timezone (-> js/Intl .DateTimeFormat .resolvedOptions .-timeZone))


(defn formatter
  ([opts]
   (-> js/Intl (.DateTimeFormat (or (:locale opts) system-locale) (clj->js opts))))
  ([locale opts]
   (-> js/Intl (.DateTimeFormat locale (clj->js opts)))))


(def date-time-opts {:year   "numeric"
                     :month  "numeric"
                     :day    "numeric"
                     :hour   "numeric"
                     :minute "numeric"})


(def date-opts {:year  "numeric"
                :month "numeric"
                :day   "numeric"})


(def yearless-date-opts {:month "numeric", :day "numeric"})


(def time-opts {:hour   "numeric"
                :minute "numeric"})


(defn date-time
  ([js-date]
   (date-time
     (formatter date-time-opts)
     js-date))
  ([formatter js-date]
   (when js-date
     (.format formatter js-date)))
  ([text-locale format-opts js-date]
   (-> (formatter text-locale format-opts)
       (date-time js-date))))

(rf/reg-sub ::format-fn
  :<- [::i18n/full-locale]
  (fn [locale [_ opts]]
    (fn [js-date]
      (when js-date
        (date-time locale opts js-date)))))


(rf/reg-sub ::date-time-fn
  :<- [::format-fn date-time-opts]
  :-> identity)


(rf/reg-sub ::date-fn
  :<- [::format-fn date-opts]
  :-> identity)


(rf/reg-sub ::yearless-date-fn
  :<- [::format-fn yearless-date-opts]
  :-> identity)


(rf/reg-sub ::time-fn
  :<- [::format-fn time-opts]
  :-> identity)


(defn in-tz-with-opts [js-date tz opts]
  (date-time (formatter (p/assoc-if opts
                                    :timeZone tz))
             js-date))


(defn date-time-in-tz [js-date tz]
  (in-tz-with-opts js-date tz date-time-opts))


(defn date-in-tz [js-date tz]
  (in-tz-with-opts js-date tz date-opts))


(defn date
  ([js-date] (date system-locale js-date))
  ([locale js-date] (date-time (formatter locale date-opts) js-date)))


(defn hour-minute-in-tz [js-date tz]
  (date-time (formatter (p/assoc-if time-opts
                                    :timeZone tz))
             js-date))


(defn interval-in-tz [start-inst end-inst tz]
  (str (date-time-in-tz start-inst tz) " - " (hour-minute-in-tz end-inst tz)))


(defn minutes-to-hour-and-minutes [minutes]
  (let [hours (int (/ minutes 60))]
    (str hours ":" (gstr/format "%02d" (- minutes (* hours 60))))))


(defn now []
  (goog.date.DateTime.))


(defn js-date->goog-date-time [js-date] (goog.date.DateTime. js-date))


(defn goog-date-time-start-of-day
  "Type check is added to the date parameter since the date-picker with max- or min-limits
  breaks if the value provided is string or nil. Therefor will default to current date.
  Will return a goog.date.DateTime object with time set to 00:00:00."
  [js-date]
  (let [evaluated-date (if (or (string? js-date)
                               (nil? js-date))
                         (now)
                         js-date)]
    (goog.date.DateTime. (.getFullYear evaluated-date) (.getMonth evaluated-date) (.getDate evaluated-date))))


(defn goog-add-days [gdate days]
  (doto gdate
    (.add (goog.date.Interval. 0 0 days))))


(defn goog-add-months [gdate months]
  (doto gdate
    (.add (goog.date.Interval. 0 months))))


(defn goog-add-minutes [gdate minutes]
  (doto gdate
    (.add (goog.date.Interval. 0 0 0 0 minutes))))


(defn date-add [days]
  (goog-add-days (now) days))


(defn add-days-to-date [js-date days]
  (js/Date. (goog-add-days (goog-date-time-start-of-day js-date) days)))


(defn add-months-to-date [js-date months]
  (js/Date. (goog-add-months (goog-date-time-start-of-day js-date) months)))


(defn add-minutes-to-date [js-date minutes]
  (js/Date. (goog-add-minutes (goog-date-time-start-of-day js-date) minutes)))


(defn end-of-day [date]
  (when date
    (js/Date. (.getFullYear date)
              (.getMonth date)
              (.getDate date)
              23
              59
              59)))


(defn today-end-of-day []
  (end-of-day (js/Date.)))


(defn start-of-day [date]
  (when date
    (js/Date. (.getFullYear date)
              (.getMonth date)
              (.getDate date))))


(defn today-start-of-day []
  (start-of-day (js/Date.)))


(defn today-at [hour minute]
  (doto (today-start-of-day)
    (.setHours hour)
    (.setMinutes minute)))


(defn tomorrow-start-of-day []
  (-> (js/Date.)
      (add-days-to-date 1)
      start-of-day))


(def calendar-date-format (goog.i18n.DateTimeFormat. "yyyy-MM-dd"))


;; Deviation is added to the resulting days. It can be used in situations like when even the ending date of an
;; event must be counted among the number of days.
(defn number-of-days-between-two-dates
  ([first-date second-date]
   (number-of-days-between-two-dates first-date second-date 0 true))
  ([first-date second-date deviation rounding?]
   (let [days (+ deviation (/ (- second-date first-date) 86400000))]
     (if rounding?
       (Math/round (double days))
       days))))

(defn before? [d1 d2]
  (when (and d1 d2)
    (< (goog.date.Date/compare d1 d2) 0)))

(defn on-or-before? [d1 d2]
  (when (and d1 d2)
    (<= (goog.date.Date/compare d1 d2) 0)))

(defn after? [d1 d2]
  (when (and d1 d2)
    (> (goog.date.Date/compare d1 d2) 0)))

(defn on-or-after? [d1 d2]
  (when (and d1 d2)
    (>= (goog.date.Date/compare d1 d2) 0)))

(defn before-now? [date]
  (before? date (now)))

(defn before-current-year? [date]
  (when (inst? date)
    (< (.getFullYear date) (.getFullYear (now)))))

(defn after-current-year? [date]
  (when (inst? date)
    (> (.getFullYear date) (.getFullYear (now)))))

(defn today? [d]
  (and (some? d) (goog.date/isSameDay d)))

(defn same-day? [d1 d2]
  (goog.date/isSameDay d1 d2))

(defn period
  "Returns the difference between two dates in milliseconds.
   Value is positive if `d1` is after `d2`, negative if `d1` is before `d2`.
   If `unit` is provided, the result is converted to that unit.
   Supported units: :days, :hours, :minutes, :seconds.
   Does not consider DST (or time zones), so value is inaccurate if the dates are across a DST change."
  ([d1 d2]
   (period d1 d2 :milliseconds))
  ([d1 d2 unit]
   (cond-> (- d1 d2)
     (= unit :days) (/ 86400000)
     (= unit :hours) (/ 3600000)
     (= unit :minutes) (/ 60000)
     (= unit :seconds) (/ 1000))))

(def numeric-opts {:hour                   "numeric"
                   :minute                 "numeric"
                   :second                 "numeric"
                   :day                    "numeric"
                   :month                  "numeric"
                   :year                   "numeric"
                   :fractionalSecondDigits 3
                   :fractionalSecond       "numeric"
                   :hourCycle              "h23"})

(defn format-to-parts
  [js-date tz]
  (->> js-date
       (.formatToParts (js/Intl.DateTimeFormat "en-US" (clj->js (assoc numeric-opts :timeZone tz))))
       js->clj
       (reduce (fn [acc {:strs [type value]}]
                 (let [kw-type (keyword type)]
                   (if (contains? numeric-opts kw-type)
                     (assoc acc kw-type (parse-long value))
                     acc)))
               {})))

(defn to-tz
  "Inverse function for 'in-tz':
   Considers input date to be in given time zone and returns new date has the same time but in browser time zone.
   e.g.:
   (to-tz #inst\"2019-12-19T12:00:00.000-05:00\" \"America/Toronto\")
      => #inst\"2019-12-19T12:00:00.000+02:00\" == #inst\"2019-12-19T10:00:00.000-00:00\""
  [js-date tz]
  (when (and (inst? js-date) (string? tz))
    (let [{:keys [year
                  month
                  day
                  hour
                  minute
                  second
                  fractionalSecond]} (format-to-parts js-date tz)]
      (js/Date. year (dec month) day hour minute second fractionalSecond))))

(defn in-tz
  "Changes input date to the same local time in the given time zone and returns the new date.
   e.g.:
   (in-tz #inst\"2019-12-19T12:00:00.000+02:00\" \"America/Toronto\")
      => #inst\"2019-12-19T12:00:00.000-05:00\" == #inst\"2019-12-19T17:00:00.000-00:00\""
  [js-date tz]
  (when (and (inst? js-date) (string? tz))
    (let [helper-date         (to-tz js-date tz)
          diff                (+ (- (.getTime js-date) (.getTime helper-date)))
          converted-date      (js/Date. (+ (.getTime js-date) diff))
          ;; When DST change occurs in user-local time zone between the original js-date and the helper date
          ;; we add or subtract an additional offset (likely 1 hour). Outputting the converted-date again to the
          ;; target tz reveals this additionally required diff.
          diff-at-target-time (- (.getTime js-date) (.getTime (to-tz converted-date tz)))]
      (js/Date. (+ (.getTime converted-date) diff-at-target-time)))))


(defn time-zone-name
  "Output the \"common name\" of the given IANA TZ db identifier like America/Toronto.
   If `tz` is `nil`, browser default time zone will be used.
   Optional opts map can contain:
   `format` short|long|shortGeneric|longGeneric
   `locale` full locale, like `\"fr-CA\"`"
  [tz {:keys [format locale]}]
  (-> (or locale preferred-locale)
      (js/Intl.DateTimeFormat (clj->js (p/assoc-if {:timeZoneName (or format "short")}
                                                   :timeZone tz)))
      (.formatToParts (js/Date.))
      js->clj
      (p/find-first {"type" "timeZoneName"})
      (get "value")))


(defn date-at-time-in-tz
  ([date hour minute tz]
   (date-at-time-in-tz date hour minute 0 tz))
  ([date hour minute second tz]
   (when (not-any? nil? [date hour minute])
     (-> (doto (js/Date. (start-of-day date))
           (.setHours hour)
           (.setMinutes minute)
           (.setSeconds second))
         (in-tz tz)))))

(defn end-of-day-in-tz [js-date tz]
  (let [{:keys [year month day]} (format-to-parts js-date tz)]
    (in-tz (js/Date. year (dec month) day 23 59 59) tz)))

(defn past?
  "Check whether the date is past the given 24h time"
  [tz [h m] date]
  (let [comparison (date-at-time-in-tz date h m tz)]
    (after? date comparison)))

(defn js-weekday-to-std [weekday]
  (if (= 0 weekday)
    7
    weekday))

(defn within?
  ([start-1 end-1 start-2 end-2]
   (and (on-or-after? start-1 start-2)
        (on-or-before? end-1 end-2)))
  ([date start end]
   (and (on-or-after? date start)
        (on-or-before? date end))))

(defn overlap? [start-1 end-1 start-2 end-2]
  (and
    (or
      ;; 1 start before 2 start, 1 end after 2 end
      (and (on-or-before? start-1 start-2)
           (on-or-after? end-1 end-2))
      ;; 1 start before 2 start, 1 end within 2
      (and (on-or-before? start-1 start-2)
           (within? end-1 start-2 end-2))
      ;; 1 start within 2, 1 end within 2
      (within? start-1 end-1 start-2 end-2)
      ;; 1 start within 2, 1 end after 2 end
      (and (within? start-1 start-2 end-2)
           (on-or-after? end-1 end-2)))
    ;; If the end of other is the start of another, there cannot be any overlap
    (not= end-1 start-2)
    (not= end-2 start-1)))


(defn hours->hours-am-or-pm [hours]
  (cond
    (nil? hours) [nil nil]
    (= hours 0) [12 :AM]
    (< hours 12) [hours :AM]
    (= hours 12) [12 :PM]
    :else [(- hours 12) :PM]))


(defn hours-am-or-pm->hours [hours am-or-pm]
  (let [am? (= am-or-pm :AM)
        pm? (= am-or-pm :PM)]
    (cond
      (nil? hours) nil
      (and (= hours 12) am?) 0
      (and (= hours 12) pm?) 12
      pm? (+ hours 12)
      :else hours)))


(defn first-day-of-week
  "Return the first day of week based on user preferred locale, where 0 represents Sunday.
   Note that the many parts of western world use Sunday and this list of locales is incomplete.
   ISO8601 defines start of week to be Monday (1 in JS Date)."
  []
  (if (contains? #{"en-CA" "en-US" "fr-CA"} preferred-locale)
    0
    1))


(defn start-of-week
  "Returns the first date of the week for a given date based on current user locale."
  [js-date]
  (let [week-start (-> (- (.getDate js-date) (.getDay js-date))
                       (+ (first-day-of-week)))]
    (js/Date. (.setDate js-date week-start))))


(defn start-of-last-week
  "Returns the Date / instant of last week's start based on current user locale."
  ([]
   (start-of-last-week (today-start-of-day)))
  ([js-date]
   (let [start-of-this-week (start-of-week js-date)
         last-week-start    (- (.getDate start-of-this-week) 7)]
     (js/Date. (.setDate start-of-this-week last-week-start)))))


(defn set-hours-and-minutes [js-date hours minutes]
  (add-minutes-to-date
    (start-of-day js-date)
    (+ minutes (* hours 60))))


(defn current-year
  []
  (.getFullYear (js/Date.)))

(defn format-time [[h m]]
  (let [time-formatter (<sub [::time-fn])]
    (time-formatter (doto (js/Date.)
                      (.setHours h)
                      (.setMinutes m)))))

(defn number-of-days-in-month
  "Return number of days in month number `month` (starts from zero)"
  [month]
  (-> (js/Date. (current-year) (inc month) 0)
      .getDate))

(def technical-date-time-formatter
  (formatter "en-CA" (clj->js {:year                   "numeric"
                               :month                  "numeric"
                               :day                    "numeric"
                               :hour                   "numeric"
                               :minute                 "numeric"
                               :second                 "numeric"
                               :timeZoneName           "short"
                               :fractionalSecondDigits 3
                               :hour12                 false})))

(def technical-date-time-formatter-short
  (formatter "en-CA" (clj->js {:year         "2-digit"
                               :month        "numeric"
                               :day          "numeric"
                               :hour         "numeric"
                               :minute       "numeric"
                               :timeZoneName "short"
                               :hour12       false})))

(defn format-technical-date-time
  "Format a JS date object like \"2024-04-01 16:06:06.555 EDT\", in browser timezone.
   If `short?` is true, format as \"24-04-01 16:06 EDT\""
  [js-date & [short?]]
  (-> (if short? technical-date-time-formatter-short technical-date-time-formatter)
      (.format js-date)
      (str/replace "," "")))

(defn get-week-number [js-date]
  (-> js-date
      js-date->goog-date-time
      .getWeekNumber))

(defn dates-of-week
  "Returns all js-dates for the week of given js-date."
  [js-date]
  (for [i (range 7)]
    (let [current-date (-> js-date
                           start-of-week
                           js-date->goog-date-time)]
      (goog-add-days current-date i)
      (js/Date. current-date))))

(defn start-of-month
  "Returns the first date of the month for a given date based on current user locale."
  [js-date]
  (js/Date. (.setDate js-date 1)))

(defn dates-of-month
  "Returns all js-dates for the month of given js-date."
  [js-date]
  (let [days-in-month (-> js-date
                          .getMonth
                          number-of-days-in-month)]
    (for [i (range days-in-month)]
      (-> js-date
          start-of-month
          js-date->goog-date-time
          (goog-add-days i)
          js/Date.))))

(defn parse-local-date
  "Parses a date string in the format 'YYYY-MM-DD' and returns a JavaScript Date object
  representing that exact date at 00:00:00 in the local timezone."
  [date-str]
  (let [[year month day] (map js/parseInt (str/split date-str #"-"))]
    (js/Date. year (dec month) day 0 0 0 0)))

(defn time-zone-info [group-tz tz-format hide-when-identical?]
  (let [locale          (<sub [::i18n/full-locale])
        opts            {:format (or tz-format "short")
                         :locale locale}
        browser-tz-name (time-zone-name nil opts)
        group-tz-name   (time-zone-name group-tz opts)]
    (when (or (not hide-when-identical?)
              (not= browser-tz-name group-tz-name))
      group-tz-name)))
